import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { IconType, IconBaseProps } from 'react-icons';
import { FaTimes, FaPaperPlane } from 'react-icons/fa';
import { useNewsletter } from '../../contexts/NewsletterContext';
import { getApiUrl } from '../../services/api';

const PopupOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  visibility: ${(props) => (props.$isVisible ? 'visible' : 'hidden')};
  transition: opacity ${({ theme }) => theme.primitives.duration.normal}
    ${({ theme }) => theme.primitives.easing.ease};
  z-index: 1000;
`;

const PopupContent = styled.div<{ $isVisible: boolean }>`
  background: ${({ theme }) => theme.semantic.color.surface.raised};
  padding: ${({ theme }) => theme.semantic.space.xl};
  border-radius: ${({ theme }) => theme.primitives.radius['2xl']};
  max-width: 500px;
  width: 90%;
  position: relative;
  border: 1px solid ${({ theme }) => theme.semantic.color.border.subtle};
  box-shadow: ${({ theme }) => theme.primitives.shadow['2xl']};
  transform: ${(props) =>
    props.$isVisible ? 'translateY(0) scale(1)' : 'translateY(-12px) scale(0.98)'};
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  transition:
    opacity ${({ theme }) => theme.primitives.duration.normal}
      ${({ theme }) => theme.primitives.easing.easeOut},
    transform ${({ theme }) => theme.primitives.duration.normal}
      ${({ theme }) => theme.primitives.easing.easeOut};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-gray);
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: var(--text-black);
    transform: rotate(90deg);
  }

  svg {
    width: 1em;
    height: 1em;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  color: var(--text-black);
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: var(--text-gray);
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: ${({ theme }) =>
    `${theme.componentTokens.input.paddingY} ${theme.componentTokens.input.paddingX}`};
  border: 1.5px solid ${({ theme }) => theme.componentTokens.input.border};
  border-radius: ${({ theme }) => theme.componentTokens.input.radius};
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  background: ${({ theme }) => theme.componentTokens.input.bg};
  color: ${({ theme }) => theme.componentTokens.input.text};
  transition: ${({ theme }) => theme.componentTokens.input.transition};

  &:focus {
    border-color: ${({ theme }) => theme.componentTokens.input.borderFocus};
    outline: none;
    box-shadow: ${({ theme }) => theme.componentTokens.input.focusRing};
    background: ${({ theme }) => theme.semantic.color.surface.page};
  }
`;

const SubmitButton = styled.button`
  background: ${({ theme }) => theme.componentTokens.button.primary.bg};
  color: ${({ theme }) => theme.componentTokens.button.primary.text};
  padding: ${({ theme }) =>
    `${theme.componentTokens.button.primary.paddingY} ${theme.componentTokens.button.primary.paddingX}`};
  border: none;
  border-radius: ${({ theme }) => theme.componentTokens.button.primary.radius};
  font-size: ${({ theme }) => theme.primitives.fontSize.sm};
  font-weight: ${({ theme }) => theme.componentTokens.button.primary.fontWeight};
  min-height: ${({ theme }) => theme.primitives.space[10]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.semantic.space.inlineGap};
  transition:
    background-color ${({ theme }) => theme.primitives.duration.fast}
      ${({ theme }) => theme.primitives.easing.easeOut},
    transform 80ms ${({ theme }) => theme.primitives.easing.easeOut};

  &:hover {
    background: ${({ theme }) => theme.componentTokens.button.primary.bgHover};
  }

  &:active {
    transform: scale(0.98);
    background: ${({ theme }) => theme.componentTokens.button.primary.bgActive};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.semantic.focusRing};
  }

  svg {
    width: 1em;
    height: 1em;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

interface IconProps extends Omit<IconBaseProps, 'icon'> {
  icon: IconType;
}

const Icon: React.FC<IconProps> = ({ icon: IconComponent, ...props }) => {
  const Component = IconComponent as React.ComponentType<IconBaseProps>;
  return (
    <IconWrapper>
      <Component {...props} />
    </IconWrapper>
  );
};

const NewsletterPopup: React.FC = () => {
  const { isOpen, closePopup } = useNewsletter();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
  });

  const handleClose = useCallback(() => {
    closePopup();
    localStorage.setItem('newsletterPopupSeen', 'true');
    localStorage.setItem('newsletterPopupLastSeen', Date.now().toString());
  }, [closePopup]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, input, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const firstInput = dialogRef.current?.querySelector<HTMLElement>('input');
    firstInput?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const apiBase = getApiUrl().replace(/\/$/, '');
      const newsletterUrl = `${apiBase}/newsletter`;
      const response = await fetch(newsletterUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao subscrever newsletter');
      }

      // Reset form
      setFormData({
        name: '',
        email: '',
        contact: '',
      });

      handleClose();
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      alert('Erro ao subscrever newsletter. Por favor, tente novamente.');
    }
  };

  return (
    <PopupOverlay $isVisible={isOpen} onClick={handleClose} role="presentation">
      <PopupContent
        ref={dialogRef}
        $isVisible={isOpen}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Subscrever newsletter"
      >
        <CloseButton onClick={handleClose} aria-label="Fechar">
          <Icon icon={FaTimes} />
        </CloseButton>
        <Title>Receba as nossas próximas viagens por email</Title>
        <Description>
          Receba em primeira mão as novidades, campanhas e experiências exclusivas da Trans Serrano!
        </Description>
        <Form onSubmit={handleSubmit}>
          <Input
            type="text"
            name="name"
            placeholder="Seu nome"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <Input
            type="email"
            name="email"
            placeholder="Seu email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <Input
            type="tel"
            name="contact"
            placeholder="Seu contacto telefónico"
            value={formData.contact}
            onChange={handleChange}
            required
          />
          <SubmitButton type="submit">
            Subscrever <Icon icon={FaPaperPlane} />
          </SubmitButton>
        </Form>
      </PopupContent>
    </PopupOverlay>
  );
};

export default NewsletterPopup;
